const React = globalThis.React;
import { CheckIcon, PhoneIcon, WifiIcon, CategoryIcon } from '../Icons.jsx';
import { CardBubblesEffect, CardSnowEffect } from '../effects/CardEffectsOptimized.jsx';
import { calculatePricing } from '../../utils/tariffUtils.js';
import { formatPriceParts } from '../../utils/helpers.js';
import { FeatureList } from './FeatureList.jsx';
import { PriceSection } from './PriceSection.jsx';
import { categoryClassMap } from '../../config/categoryTags.js';

// Performance: React.memo verhindert unnötige Re-Renders
export const TariffCard = React.memo(function TariffCard(props) {
  const {
    title,
    category,
    dataLabel,
    promoLabel,
    startsOn,
    expiresOn,
    showValidityLeft,
    features,
    price,
    promoPrice,
    connectionFee,
    segment,
    group,
    originalPrice: kombiOriginalPrice,
    isComboDiscounted,
    comboDiscount,
    isActive,
    onToggle,
    activeEvent,
    effects = {},
    effectConfig = {},
    isSelected,
    onSelectToggle,
    onPrint
  } = props;

  const isSnowEvent = ['winter', 'christmas', 'christmas2'].includes(activeEvent);
  const showCardSnow = effects.cardSnow !== false;
  const showCardBubbles = effects.cardBubbles !== false || activeEvent === 'spring';
  const featuresList = features || [];
  const detailsOpen = !!isActive;
  const isPrepaid = segment === 'prepaid';
  const isMyHome = segment === 'myhome';
  const isBlue = segment === 'blue';

  const pricing = calculatePricing({
    price,
    promoPrice,
    kombiOriginalPrice,
    isComboDiscounted,
    segment,
    promoLabel,
  });

  const {
    displayPrice,
    regularPrice,
    hasPromo,
    hasMyHomePromo,
    hasSavings,
    savingsPercent,
    savingsMonthly,
    savingsYear,
    savingsTwoYears,
    savingsDaily,
  } = pricing;

  const effectivePrice = displayPrice;
  const originalPrice = regularPrice;
  const { intPart, decPart } = formatPriceParts(effectivePrice);

  const termMonths = isPrepaid ? 1 : 24;
  const totalCost = effectivePrice * termMonths + (isPrepaid ? 0 : (connectionFee || 0));
  const phoneIconOffset = 'top-[52px]';

  const wrapperExtra = isPrepaid
    ? 'prepaid-ring'
    : (isComboDiscounted
        ? ''
        : (category === 'Promo Aktion' ? 'card-wrapper-promo' : ''));

  return (
    <article
      role="article"
      aria-labelledby={`tariff-${props.id}-title`}
      className={`card-wrapper flex flex-col text-sky-50 ${wrapperExtra} ${isActive ? 'card-active' : ''} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/20`}
    >
      <div className="card-header relative flex-shrink-0">

        <div className="px-5 pt-3 pb-14 min-h-[240px]">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              {isComboDiscounted ? (
                <div className="kombi-badge-neutral leading-tight">
                  <div>Kombi-Vorteil</div>
                  <div>{typeof comboDiscount === 'string' ? comboDiscount : '50%'} Rabatt</div>
                </div>
              ) : null}

              {showValidityLeft && (startsOn || expiresOn) && !isComboDiscounted ? (
                <div className="validity-badge-left">
                  {startsOn && expiresOn ? (
                    <span>{startsOn} – {expiresOn}</span>
                  ) : startsOn ? (
                    <span>ab {startsOn}</span>
                  ) : expiresOn ? (
                    <span>bis {expiresOn}</span>
                  ) : null}
                </div>
              ) : null}

              {!showValidityLeft && group === 'promo' && !isComboDiscounted && (startsOn || expiresOn) ? (
                <div className="text-[0.68rem] text-sky-100/80 mt-0">
                  {startsOn && expiresOn ? (
                    <span>Gültig {startsOn} – {expiresOn}</span>
                  ) : startsOn ? (
                    <span>Gültig ab {startsOn}</span>
                  ) : expiresOn ? (
                    <span>Gültig bis {expiresOn}</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {category && category !== 'Ohne Rabatt' && !isComboDiscounted && !isMyHome ? (
              <div
                className={`px-4 py-1 rounded-full text-[0.75rem] font-semibold badge-category ${
                  categoryClassMap[category] || ''
                } text-sky-50 flex items-center gap-1.5`}
              >
                {category && <CategoryIcon category={category} />}
                <span>{category}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-[44px] relative flex flex-col items-center text-center w-full">
            <div className="flex flex-col items-center text-center gap-1">
              <div className="flex items-baseline justify-center gap-1.5">
                <span
                  id={`tariff-${props.id}-title`}
                  className="o2-prefix-glow font-heading text-[0.95rem] sm:text-[1.05rem] font-bold tracking-[0.04em]"
                >
                  {title}
                </span>
              </div>

              <div className="mt-3 flex items-end justify-center gap-2">
                {dataLabel === 'unbegrenzt' ? (
                  <>
                    <span className="liquid-glow-text text-[2.6rem] font-extrabold leading-none">
                      ∞
                    </span>
                    <span className="liquid-glow-text text-[0.85rem] font-semibold leading-none mb-1 tracking-wide">
                      unbegrenzt
                    </span>
                  </>
                ) : isPrepaid && typeof dataLabel === 'string' && dataLabel.includes('Cent') ? (
                  <span className="liquid-glow-text text-[2.2rem] font-extrabold leading-none">
                    {dataLabel}
                  </span>
                ) : segment === 'myhome' && typeof dataLabel === 'string' ? (
                  <>
                    <span className="liquid-glow-text text-[3.1rem] font-extrabold leading-none">
                      {dataLabel.replace(' MBit/s', '')}
                    </span>
                    <span className="text-[1.35rem] font-semibold leading-none mb-1">
                      MBit/s
                    </span>
                  </>
                ) : typeof dataLabel === 'string' && dataLabel.match(/[a-zA-Z]/) ? (
                  <span className="liquid-glow-text text-[1.4rem] font-extrabold leading-none pb-1">
                    {dataLabel}
                  </span>
                ) : (
                  <>
                    <span className="liquid-glow-text text-[3.1rem] font-extrabold leading-none">
                      {dataLabel}
                    </span>
                    <span className="text-[1.35rem] font-semibold leading-none mb-1">
                      GB+
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className={`absolute right-0 ${phoneIconOffset} mr-2 flex items-center opacity-70`}>
              {segment === 'myhome' ? <WifiIcon /> : <PhoneIcon />}
            </div>
          </div>
        </div>

        {isSnowEvent && showCardSnow && <CardSnowEffect effectConfig={effectConfig} />}
      </div>

      {/* Modern Liquid Glass Waves (overlaps the junction) */}
      <div className="w-full h-[36px] -mt-[36px] relative z-[15] overflow-hidden pointer-events-none">
        <svg viewBox="0 0 1200 100" preserveAspectRatio="none" className="absolute bottom-0 w-[200%] h-full" style={{animation: 'wave-drift 12s linear infinite'}}>
          <path d="M0,50 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,100 L0,100 Z" fill="rgba(6, 182, 212, 1)" />
        </svg>
        <svg viewBox="0 0 1200 100" preserveAspectRatio="none" className="absolute bottom-0 w-[200%] h-[28px]" style={{animation: 'wave-drift 8s linear infinite reverse', opacity: 0.8}}>
          <path d="M0,50 C200,10 400,90 600,50 C800,-10 1000,90 1200,50 L1200,100 L0,100 Z" fill="rgba(56, 189, 248, 1)" />
        </svg>
        <svg viewBox="0 0 1200 100" preserveAspectRatio="none" className="absolute bottom-0 w-[200%] h-[20px]" style={{animation: 'wave-drift 6s linear infinite'}}>
          <path d="M0,50 C100,60 300,10 600,50 C900,90 1100,20 1200,50 L1200,100 L0,100 Z" fill="rgba(255, 255, 255, 0.4)" />
        </svg>
      </div>

      <div className="card-middle">
        <FeatureList features={featuresList} />
      </div>

      <div
        className="card-footer px-7 pt-5 pb-6 text-center text-sky-50 relative cursor-pointer flex flex-col bg-[#010b24] overflow-visible"
        onClick={() => onToggle && onToggle(!isActive)}
      >
        {showCardBubbles && <CardBubblesEffect effectConfig={effectConfig} />}

        <div className={`card-footer-actions always-visible ${isActive ? 'expanded' : ''} mt-auto`}>
          <button
            className={`card-footer-btn ${isSelected ? 'selected' : ''}`}
            onClick={(e) => { e.stopPropagation(); onSelectToggle && onSelectToggle(); }}
            title={isSelected ? 'Aus Auswahl entfernen' : 'Zur Auswahl hinzufügen'}
          >
            <svg viewBox="0 0 24 24">
              {isSelected ? (
                <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              ) : (
                <path d="M12 5v14M5 12h14" />
              )}
            </svg>
          </button>
          <button
            className="card-footer-btn"
            onClick={(e) => { e.stopPropagation(); onPrint && onPrint(); }}
            title="Drucken"
          >
            <svg viewBox="0 0 24 24">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
          </button>
          <button
            className="card-footer-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (props.onEditRequest) props.onEditRequest(props.id);
            }}
            title="Bearbeiten"
          >
            <svg viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>

        <PriceSection
          detailsOpen={detailsOpen}
          intPart={intPart}
          decPart={decPart}
          hasMyHomePromo={hasMyHomePromo}
          hasPromo={hasPromo}
          originalPrice={originalPrice}
          hasSavings={hasSavings}
          savingsPercent={savingsPercent}
          savingsMonthly={savingsMonthly}
          savingsDaily={savingsDaily}
          savingsYear={savingsYear}
          savingsTwoYears={savingsTwoYears}
          isMyHome={isMyHome}
          isPrepaid={isPrepaid}
          connectionFee={connectionFee}
          isComboDiscounted={isComboDiscounted}
          totalCost={totalCost}
          effectivePrice={effectivePrice}
          termMonths={termMonths}
          title={title}
          onEditRequest={props.onEditRequest}
          id={props.id}
        />
      </div>
    </article>
  );
});

